import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import bubble from '../../assets/images/bubble.png';

// Desktop-only cursor: the pointer becomes a bubble, and moving it leaves a trail of
// small bubbles that float up and pop. Everything is driven straight off pointer events
// and the Web Animations API (no React state), so it costs nothing per mouse move.

const TRAIL_SPACING_PX = 16; // distance the cursor travels between trail bubbles
const MAX_TRAIL_BUBBLES = 40;
const CURSOR_SIZE_PX = 34;
const INTERACTIVE = 'a, button, [role="button"], input, textarea, select, label, summary';

const rand = (min: number, max: number) => min + Math.random() * (max - min);

const BubbleCursor = () => {
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Leave touch screens and anyone who's asked for less motion on the normal cursor.
    const isDesktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const wantsReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cursor = cursorRef.current;
    const layer = layerRef.current;
    if (!isDesktopPointer || wantsReducedMotion || !cursor || !layer) return;

    // Only hide the native cursor once we know ours is running.
    document.documentElement.classList.add('bubble-cursor');

    let lastX = 0;
    let lastY = 0;
    let lastSpawnX = 0;
    let lastSpawnY = 0;
    let hasMoved = false;
    let live = 0;

    // A single bubble: fades in, drifts up with a little wobble, then bulges and pops.
    const spawnBubble = (x: number, y: number, size: number, opts?: { burst?: boolean }) => {
      if (live >= MAX_TRAIL_BUBBLES) return;
      live += 1;

      const el = document.createElement('img');
      el.src = bubble;
      el.alt = '';
      el.draggable = false;
      Object.assign(el.style, {
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
        width: `${size}px`,
        height: `${size}px`,
        objectFit: 'contain',
        pointerEvents: 'none',
        willChange: 'transform, opacity',
      });
      layer.appendChild(el);

      const at = (dx: number, dy: number, scale: number) =>
        `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${scale})`;

      let keyframes: Keyframe[];
      let duration: number;
      if (opts?.burst) {
        // Click burst: fly outward quickly, then pop.
        const angle = rand(0, Math.PI * 2);
        const dist = rand(18, 44);
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        duration = rand(380, 620);
        keyframes = [
          { transform: at(0, 0, 0.4), opacity: 0.95 },
          { transform: at(dx * 0.85, dy * 0.85, 1), opacity: 0.95, offset: 0.7 },
          { transform: at(dx, dy, 1.4), opacity: 0 },
        ];
      } else {
        const rise = rand(40, 90);
        const sway = rand(-22, 22);
        duration = rand(900, 1600);
        // Each bubble starts full size at the cursor and shrinks as it floats away, so
        // the trail tapers from big (newest, by the cursor) to small (oldest, behind).
        keyframes = [
          { transform: at(0, 0, 0.6), opacity: 0 },
          { transform: at(sway * 0.3, -rise * 0.2, 1), opacity: 0.95, offset: 0.08 },
          { transform: at(-sway * 0.4, -rise * 0.55, 0.6), opacity: 0.9, offset: 0.55 },
          { transform: at(sway, -rise * 0.9, 0.38), opacity: 0.9, offset: 0.88 },
          // pop: a quick bulge, then gone
          { transform: at(sway, -rise, 0.62), opacity: 0 },
        ];
      }

      const animation = el.animate(keyframes, { duration, easing: 'ease-out', fill: 'forwards' });
      const cleanup = () => {
        el.remove();
        live -= 1;
      };
      animation.onfinish = cleanup;
      animation.oncancel = cleanup;
    };

    const setState = (state: '' | 'hover' | 'down') => {
      cursor.dataset.state = state;
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      lastX = e.clientX;
      lastY = e.clientY;
      cursor.style.transform = `translate3d(${lastX}px, ${lastY}px, 0)`;

      if (!hasMoved) {
        hasMoved = true;
        lastSpawnX = lastX;
        lastSpawnY = lastY;
        cursor.style.opacity = '1';
        return;
      }

      const traveled = Math.hypot(lastX - lastSpawnX, lastY - lastSpawnY);
      if (traveled >= TRAIL_SPACING_PX) {
        lastSpawnX = lastX;
        lastSpawnY = lastY;
        spawnBubble(lastX + rand(-5, 5), lastY + rand(-5, 5), rand(12, 22));
      }
    };

    const onOver = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const overInteractive = (e.target as Element | null)?.closest?.(INTERACTIVE);
      setState(overInteractive ? 'hover' : '');
    };

    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      setState('down');
      for (let i = 0; i < 6; i += 1) spawnBubble(e.clientX, e.clientY, rand(7, 14), { burst: true });
    };

    const onUp = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const overInteractive = (e.target as Element | null)?.closest?.(INTERACTIVE);
      setState(overInteractive ? 'hover' : '');
    };

    const hide = () => {
      cursor.style.opacity = '0';
    };
    const show = () => {
      if (hasMoved) cursor.style.opacity = '1';
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerover', onOver, { passive: true });
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    document.documentElement.addEventListener('mouseleave', hide);
    document.documentElement.addEventListener('mouseenter', show);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerover', onOver);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      document.documentElement.removeEventListener('mouseleave', hide);
      document.documentElement.removeEventListener('mouseenter', show);
      document.documentElement.classList.remove('bubble-cursor');
      layer.replaceChildren();
    };
  }, []);

  return (
    <>
      {/* Trail bubbles are appended here */}
      <Box
        ref={layerRef}
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 9998,
          overflow: 'hidden',
        }}
      />
      {/* The cursor itself: the outer box tracks the pointer, the inner bubble scales. */}
      <Box
        ref={cursorRef}
        aria-hidden
        data-state=""
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
          zIndex: 9999,
          opacity: 0,
          transition: 'opacity 0.2s ease',
          '& img': {
            position: 'absolute',
            width: `${CURSOR_SIZE_PX}px`,
            height: `${CURSOR_SIZE_PX}px`,
            left: `${-CURSOR_SIZE_PX / 2}px`,
            top: `${-CURSOR_SIZE_PX / 2}px`,
            objectFit: 'contain',
            transition: 'transform 0.18s ease',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25))',
          },
          '&[data-state="hover"] img': { transform: 'scale(1.5)' },
          '&[data-state="down"] img': { transform: 'scale(0.8)' },
        }}
      >
        <img src={bubble} alt="" draggable={false} />
      </Box>
    </>
  );
};

export default BubbleCursor;
